/**
 * marketing-hook-library.ts — reusable hook library that BDRs can pull into
 * outreach cadences. Distinct from per-event hooks (those live in
 * marketing-hook-generator). Entries here are content marketing maintains
 * across all cadences.
 */

export type HookKind = "email" | "linkedin" | "content";
export type HookStatus = "draft" | "approved" | "trending" | "new";

export interface LibraryHook {
  id: string;
  kind: HookKind;
  title: string;
  snippet: string;
  tags: string[];          // segment / persona / stage / theme
  status: HookStatus;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

const KEY = "aip:marketing-hook-library:v1";

const SEED: LibraryHook[] = [
  // ── Email (4) ─────────────────────────────────────────────────────────────
  { id: "h-em-01", kind: "email", title: "Profit+ pilot saved Caldera Industrial $X in 90 days",
    snippet: "Hi [Prospect Name], when Caldera Industrial ran a 90-day Profit+ pilot, they pulled $X out of their cost-savings backlog. Worth a 20-minute walkthrough to see the format we used.",
    tags: ["industrial","CFO","stage:awareness","theme:ROI"], status: "approved", usage_count: 12,
    created_at: "2026-04-01", updated_at: "2026-04-22" },
  { id: "h-em-02", kind: "email", title: "Executive shift in mid-market manufacturing",
    snippet: "[Prospect Name] — three of the last five mid-market industrial CFOs we spoke to said cost-savings program tracking has moved up to a board metric. Quick 1:1 worth the time?",
    tags: ["industrial","executive","stage:cold","theme:trend"], status: "approved", usage_count: 9,
    created_at: "2026-03-12", updated_at: "2026-04-15" },
  { id: "h-em-03", kind: "email", title: "Where you stand today — diagnostic offer",
    snippet: "We built a 5-question diagnostic that benchmarks your cost-savings program tracking against the Top-10 industrial OEMs. 3 minutes — worth running it?",
    tags: ["all-segments","operations-VP","stage:nurture","theme:diagnostic"], status: "approved", usage_count: 18,
    created_at: "2026-02-18", updated_at: "2026-04-30" },
  { id: "h-em-04", kind: "email", title: "90-day Profit+ pilot offer",
    snippet: "We're opening four 90-day Profit+ pilot slots in Q3 for Tier-1 automotive and industrial mid-market. Two are spoken for. Want a walkthrough before the rest go?",
    tags: ["automotive","industrial","stage:close","theme:scarcity"], status: "trending", usage_count: 22,
    created_at: "2026-04-08", updated_at: "2026-05-03" },

  // ── LinkedIn (4) ──────────────────────────────────────────────────────────
  { id: "h-li-01", kind: "linkedin", title: "Founder voice — Bryan on M-PACT methodology",
    snippet: "Bryan: \"After 25 years selling into manufacturing, the same conversation kept happening — cost-savings was a board priority but the tracking was a spreadsheet. M-PACT is what we built so the tracking stops being the bottleneck.\"",
    tags: ["founder-voice","M-PACT","theme:thought-leadership"], status: "approved", usage_count: 7,
    created_at: "2026-03-20", updated_at: "2026-04-10" },
  { id: "h-li-02", kind: "linkedin", title: "Yvonne POV — purchasing-led entry pattern",
    snippet: "Yvonne: \"The fastest path into a Tier-1 supplier we've ever found is purchasing. They feel the cost-program pain first and they're the ones who introduce us to finance.\"",
    tags: ["automotive","purchasing","persona:purchasing","theme:playbook"], status: "trending", usage_count: 11,
    created_at: "2026-04-05", updated_at: "2026-05-01" },
  { id: "h-li-03", kind: "linkedin", title: "Customer story — Caldera Industrial (anonymized)",
    snippet: "A mid-market industrial OEM took our 90-day Profit+ pilot, surfaced 14 cost-savings projects that were stuck, and finished the pilot with $X in committed savings. Anonymized writeup on request.",
    tags: ["industrial","case-study","theme:proof"], status: "new", usage_count: 3,
    created_at: "2026-05-01", updated_at: "2026-05-04" },
  { id: "h-li-04", kind: "linkedin", title: "Trend signal — mfg-auto convergence",
    snippet: "Three of our last four conversations were with industrial mid-market CFOs whose plants now ship to Tier-1 automotive. Same cost-savings discipline, two industries colliding. That's where we're focused.",
    tags: ["industrial","automotive","trend","theme:positioning"], status: "approved", usage_count: 6,
    created_at: "2026-03-30", updated_at: "2026-04-25" },

  // ── Content (4) ───────────────────────────────────────────────────────────
  { id: "h-co-01", kind: "content", title: "M-PACT methodology one-pager",
    snippet: "Single-page PDF: the 10 checkpoints, the hard-kill rules, and how Profit+ scores each one. Drop in any cadence step where the prospect asked \"what's the framework?\"",
    tags: ["M-PACT","reference","stage:awareness","format:PDF"], status: "approved", usage_count: 31,
    created_at: "2026-01-15", updated_at: "2026-04-20" },
  { id: "h-co-02", kind: "content", title: "Helix demo video — 90 seconds",
    snippet: "90-second Loom of the AIP helix walking through one Composite score. Best for warm prospects who've heard the pitch and want to see the tool.",
    tags: ["demo","stage:evaluation","format:video"], status: "approved", usage_count: 24,
    created_at: "2026-02-10", updated_at: "2026-04-22" },
  { id: "h-co-03", kind: "content", title: "Profit+ case study — Caldera Industrial",
    snippet: "Full case study (anonymized). 6-page PDF with the diagnostic, the engagement, the savings surfaced, the close. Approved for all manufacturing segments.",
    tags: ["case-study","industrial","stage:close","format:PDF"], status: "approved", usage_count: 19,
    created_at: "2026-02-28", updated_at: "2026-04-18" },
  { id: "h-co-04", kind: "content", title: "Top-10 account benchmark — industrial mid-market",
    snippet: "Internal benchmark deck showing where the Top-10 ABM accounts sit on the M-PACT helix today. AE-only — do not send out raw. Use it to frame discovery questions.",
    tags: ["benchmark","internal","ABM","stage:planning"], status: "draft", usage_count: 4,
    created_at: "2026-04-25", updated_at: "2026-05-05" },
];

export function loadLibrary(): LibraryHook[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* fall through */
  }
  saveLibrary(SEED);
  return SEED;
}

export function saveLibrary(hooks: LibraryHook[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(hooks));
  } catch {
    /* ignore */
  }
}

export function incrementUsage(id: string): LibraryHook[] {
  const lib = loadLibrary();
  const next = lib.map((h) => (h.id === id ? { ...h, usage_count: h.usage_count + 1, updated_at: new Date().toISOString() } : h));
  saveLibrary(next);
  return next;
}
